import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getAdminCoursesApi, AdminCourseListItem } from "../../features/analytics/api/adminAnalytics.api"
import { HBarChart, VBarChart, ChartCard } from "./Charts"
import { ChevronRightIcon, ExclamationTriangleIcon, AcademicCapIcon } from "@heroicons/react/16/solid"

const LIMIT = 5

const Stars = ({ rating }: { rating: number }) => (
  <span className="inline-flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} className={`text-[11px] ${i < Math.floor(rating) ? "text-amber-400" : "text-gray-200"}`}>★</span>
    ))}
    <span className="ml-1 text-xs text-gray-500">{rating}</span>
  </span>
)

const StatusBadge = ({ active }: { active: boolean }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
    active ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-gray-100 text-gray-500"
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-gray-400"}`} />
    {active ? "Активен" : "Неактивен"}
  </span>
)

export const CoursesListPage = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<AdminCourseListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    setLoading(true)
    setError(false)
    getAdminCoursesApi({ limit: 100, offset: 0 })
      .then((res) => {
        setItems(res.data.items)
        setTotal(res.data.total)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  if (error) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400 mb-2">Не удалось загрузить курсы</p>
        <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
          Повторить
        </button>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {[1, 2].map((i) => <div key={i} className="bg-white rounded-2xl border border-gray-100 h-52 animate-pulse" />)}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 h-64 animate-pulse" />
      </div>
    )
  }

  const totalPages = Math.max(1, Math.ceil(items.length / LIMIT))
  const pageItems = items.slice((page - 1) * LIMIT, page * LIMIT)

  const top5 = [...items].sort((a, b) => b.views - a.views).slice(0, 5)
  const top5revenue = [...items].sort((a, b) => b.revenue - a.revenue).slice(0, 5)

  return (
    <div className="space-y-5">
      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Топ курсов по просмотрам">
          <HBarChart
            data={top5.map((c) => ({ label: c.title.slice(0, 18), value: c.views }))}
            color="#3b82f6"
          />
        </ChartCard>
        <ChartCard title="Топ курсов по выручке (сом)">
          <VBarChart
            data={top5revenue.map((c) => ({ label: c.title.slice(0, 10), value: c.revenue }))}
            color="#8b5cf6"
          />
        </ChartCard>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b bg-gray-50/60 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Все курсы</h3>
          <span className="text-xs text-gray-400">{total} курсов</span>
        </div>

        {items.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <AcademicCapIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Курсов пока нет</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/40">
                  {["Курс", "Просмотры", "Покупки", "Конверсия", "Рейтинг", "Завершаемость", "Выручка", "Статус", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pageItems.map((c) => (
                  <tr key={c.course_id}
                    onClick={() => navigate(`/admin/analytics/courses/${c.course_id}`)}
                    className="hover:bg-blue-50/40 transition-colors cursor-pointer group">
                    <td className="px-4 py-3.5 min-w-[160px]">
                      <p className="font-medium text-gray-800 leading-tight">{c.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{c.author_name}</p>
                    </td>
                    <td className="px-4 py-3.5 text-gray-700 whitespace-nowrap">
                      <span className="font-medium">{c.views.toLocaleString("ru")}</span>
                      <span className="text-xs text-gray-400 block">{c.unique_visitors.toLocaleString("ru")} уник.</span>
                    </td>
                    <td className="px-4 py-3.5 text-gray-700">{c.purchases.toLocaleString("ru")}</td>
                    <td className="px-4 py-3.5">
                      <span className={`font-semibold ${c.conversion_rate >= 7 ? "text-emerald-600" : c.conversion_rate >= 5 ? "text-amber-600" : "text-red-500"}`}>
                        {c.conversion_rate}%
                      </span>
                    </td>
                    <td className="px-4 py-3.5"><Stars rating={c.avg_rating} /></td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${c.completion_rate >= 70 ? "bg-emerald-500" : c.completion_rate >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                            style={{ width: `${c.completion_rate}%` }} />
                        </div>
                        <span className="text-xs text-gray-600">{c.completion_rate}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-gray-700 font-medium whitespace-nowrap">
                      {(c.revenue / 1000).toFixed(0)}K сом
                    </td>
                    <td className="px-4 py-3.5"><StatusBadge active={c.is_active} /></td>
                    <td className="px-4 py-3.5">
                      <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {items.length > 0 && (
          <div className="px-5 py-3.5 border-t bg-gray-50/40 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              Стр. <span className="font-medium text-gray-600">{page}</span> из{" "}
              <span className="font-medium text-gray-600">{totalPages}</span>
            </span>
            <div className="flex gap-1.5">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ← Пред.
              </button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                След. →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
